import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { Save, Undo2 } from "lucide-react";

import { businessApi } from "../../../services/api";
import {
  Button, ConfirmDialog, ErrorState, Field, Input, Notice, RouteTabs, Select, SkeletonRows, Surface, plainError,
} from "../../../components/ui";
import HRPageShell from "./components/HRPageShell";
import StatutoryRulesPanel from "./components/StatutoryRulesPanel";
import PayRulesPanel from "./components/PayRulesPanel";
import AttendancePayRulesPanel from "./components/AttendancePayRulesPanel";
import WorkStandardPanel from "./components/WorkStandardPanel";
import SettingsRelatedLinks from "./components/SettingsRelatedLinks";

// Report 58 (M7): six tabs, each named by what the operator is setting up. Route-backed so a link, back and forward land
// on the same tab. Pay schedules stay in HR › Payroll › Pay schedules.
export const SETTINGS_TABS = [
  { key: "general", label: "General" },
  { key: "work", label: "Work schedule" },
  { key: "attendance-pay", label: "Attendance pay" },
  { key: "pay-rules", label: "Overtime, holidays & night pay" },
  { key: "leave", label: "Leave" },
  { key: "government", label: "Contributions & tax" },
];
// Older links used ?section=…; they still land on the right tab.
const LEGACY_SECTIONS = { general: "general", attendance: "attendance-pay", payroll: "general", leave: "leave", statutory: "government" };
const BASE = "/business/hr/settings";

const TIMEZONES = [
  "Asia/Manila", "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai",
  "America/New_York", "America/Chicago", "America/Los_Angeles",
  "Europe/London", "Europe/Berlin", "UTC",
];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function buildSettingsForm(settings = {}) {
  return {
    employee_code_prefix: settings.employee_code_prefix || "EMP",
    timezone: settings.timezone || "Asia/Manila",
    default_leave_year_start: settings.default_leave_year_start ?? 1,
  };
}

// Settings that attendance, leave and payroll read the next time they work something out: they change future results
// for everyone, so they are reviewed before saving. The employee-code prefix only affects codes given out later.
// (Report 58: the standard week and paid hours moved to their own dated panel on Work schedule.)
const REVIEWED_SETTINGS = [
  ["timezone", "Timezone", (v) => v || "-"],
  ["default_leave_year_start", "Leave year starts", (v) => MONTHS[Number(v) - 1] || "-"],
];

export function reviewedSettingChanges(before, after) {
  const norm = (value) => (value === null || value === undefined || value === "" ? "" : Number.isFinite(Number(value)) ? String(Number(value)) : String(value));
  const same = (a, b) => norm(a) === norm(b);
  return REVIEWED_SETTINGS
    .filter(([key]) => !same(before?.[key], after?.[key]))
    .map(([key, label, show]) => ({ label, value: `${show(norm(before?.[key]))} → ${show(norm(after?.[key]))}` }));
}

/** Field-level checks before anything is sent; the server checks the same and more. */
export function settingsErrors(form) {
  const errors = {};
  const prefix = String(form.employee_code_prefix || "").trim();
  if (!prefix) errors.employee_code_prefix = "Enter a prefix, for example EMP.";
  else if (!/^[A-Za-z0-9]{1,10}$/.test(prefix)) errors.employee_code_prefix = "Use 1 to 10 letters or numbers, with no spaces or symbols.";
  return errors;
}

function Section({ title, purpose, children, aside = null }) {
  return (
    <Surface padded as="section" aria-label={title}>
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
        <h2 className="text-section-title text-foreground">{title}</h2>
        {aside}
      </div>
      {purpose ? <p className="mt-0.5 max-w-[75ch] text-body-sm text-secondary">{purpose}</p> : null}
      <div className="mt-4">{children}</div>
    </Surface>
  );
}

function EditableSettings({ tab, settings, onSave }) {
  const [form, setForm] = useState(() => buildSettingsForm(settings));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [result, setResult] = useState(null);
  const [review, setReview] = useState(null);

  useEffect(() => {
    setForm(buildSettingsForm(settings));
    setDirty(false);
    setErrors({});
  }, [settings]);

  const setValue = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setDirty(true);
    setResult(null);
  };
  const field = (key) => (event) => setValue(key, event.target.value);

  const payload = () => ({
    employee_code_prefix: String(form.employee_code_prefix || "").trim().toUpperCase(),
    timezone: form.timezone,
    default_leave_year_start: Number(form.default_leave_year_start),
  });

  const persist = async (data) => {
    await onSave(data);
    setDirty(false);
    setReview(null);
    setResult({ tone: "success", text: "HR settings saved." });
  };

  const handleSave = async () => {
    const found = settingsErrors(form);
    setErrors(found);
    if (Object.keys(found).length) {
      setResult({ tone: "danger", text: "Check the highlighted settings." });
      return;
    }
    const data = payload();
    const changes = reviewedSettingChanges(buildSettingsForm(settings), data);
    if (changes.length) {
      setReview({ data, changes });
      return;
    }
    setSaving(true);
    try {
      await persist(data);
    } catch (err) {
      setResult({ tone: "danger", text: plainError(err, "The settings were not saved. Try again.") });
    } finally {
      setSaving(false);
    }
  };

  const nextEmployeeCode = `${String(form.employee_code_prefix || "EMP").toUpperCase()}-${new Date().getFullYear()}-${String(settings?.employee_code_next_seq ?? 1).padStart(5, "0")}`;
  const timezoneChanged = review?.changes?.some((change) => change.label === "Timezone");

  return (
    <div className="space-y-4">
      {tab === "general" ? (
        <>
          <Section title="Employee codes" purpose="The prefix new employee codes start with. Codes already given out do not change.">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Code prefix" required error={errors.employee_code_prefix} hint="1 to 10 letters or numbers.">
                <Input value={form.employee_code_prefix || ""} onChange={field("employee_code_prefix")} maxLength={10} autoComplete="off" placeholder="EMP" />
              </Field>
              <div className="flex flex-col gap-1.5">
                <span className="text-label text-secondary">Next code</span>
                <span className="flex h-control items-center break-all text-body font-medium tabular-nums text-foreground">{nextEmployeeCode}</span>
              </div>
            </div>
          </Section>
          <Section title="Timezone" purpose="The business's local time. Attendance, leave and payroll decide which calendar day a clock-in belongs to from this.">
            <Field label="Timezone" className="max-w-sm" hint="Change it only if the business has moved. Times already recorded keep their moment; days are read again in the new timezone.">
              <Select value={form.timezone || ""} onChange={field("timezone")}>
                {TIMEZONES.map((zone) => <option key={zone} value={zone}>{zone}</option>)}
              </Select>
            </Field>
          </Section>
          <Section title="Where the rest of HR is set up" purpose="Settings that live with the work they control.">
            <ul className="max-w-[75ch] space-y-1 text-body-sm text-secondary">
              <li><span className="font-medium text-foreground">Pay schedules and pay days</span>: Payroll › Pay schedules.</li>
              <li><span className="font-medium text-foreground">Official holidays and closure days</span>: Administration › Calendar.</li>
              <li><span className="font-medium text-foreground">Business deductions and loans</span>: HR › Deductions.</li>
              <li><span className="font-medium text-foreground">Shifts and each employee&apos;s weekly schedule</span>: HR › Shifts.</li>
            </ul>
            <SettingsRelatedLinks items={[
              { to: "/business/hr/payroll", label: "Manage pay schedules" },
              { to: "/business/administration/calendar", label: "Manage holidays and closures" },
              { to: "/business/hr/deductions", label: "Manage deductions" },
            ]} />
          </Section>
        </>
      ) : null}

      {tab === "leave" ? (
        <>
          <Section title="Leave year" purpose="The month leave balances start again each year.">
            <Field label="Leave year starts in" className="max-w-sm" hint="Balances already given out are not recalculated; the next year starts in the month you choose.">
              <Select value={form.default_leave_year_start ?? 1} onChange={field("default_leave_year_start")}>
                {MONTHS.map((month, index) => <option key={month} value={index + 1}>{month}</option>)}
              </Select>
            </Field>
          </Section>
          <Section title="What a leave day is" purpose="How a leave request is counted and paid.">
            <ul className="max-w-[75ch] list-disc space-y-1 pl-5 text-body-sm text-secondary">
              <li>A leave day is one working day on the employee&apos;s own schedule. Rest days, official holidays and closure days in the dates asked for are not counted.</li>
              <li>Paid leave pays a fixed salary as usual and a daily rate for each day. For an hourly employee a paid leave day is that day&apos;s scheduled paid hours (the shift less any unpaid break), or the standard day&apos;s paid hours when there is no shift.</li>
              <li>Each leave type says whether it is paid and how many days it gives a year.</li>
              <li>Apparify does not check the service incentive leave the law requires for some businesses; set up a leave type for it if it applies to yours.</li>
            </ul>
            <SettingsRelatedLinks items={[
              { to: "/business/hr/leave", label: "Manage leave types and requests" },
            ]} />
          </Section>
        </>
      ) : null}

      {result ? <Notice tone={result.tone} role={result.tone === "danger" ? "alert" : "status"}>{result.text}</Notice> : null}
      {review ? (
        <ConfirmDialog
          size="md"
          title="Save these HR settings?"
          facts={review.changes}
          consequence="Attendance, leave and payroll use the new values the next time they are worked out, for everyone. Payroll already with Finance or released is not changed."
          confirmLabel="Save settings"
          cancelLabel="Back to editing"
          failureTitle="Not saved"
          onConfirm={() => persist(review.data)}
          onClose={() => setReview(null)}
        >
          {timezoneChanged ? (
            <Notice tone="warning">A clock-in near midnight can move to the day before or after. Payrolls not yet with Finance are worked out again in the new timezone when they are recalculated.</Notice>
          ) : null}
        </ConfirmDialog>
      ) : null}

      {dirty ? (
        <div className="sticky bottom-3 z-10 flex flex-col gap-3 rounded-panel border border-default bg-surface-raised px-4 py-3 shadow-menu sm:flex-row sm:items-center sm:justify-between">
          <p className="text-body-sm font-medium text-foreground">Unsaved changes</p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button icon={Undo2} disabled={saving} onClick={() => { setForm(buildSettingsForm(settings)); setErrors({}); setDirty(false); }}>Discard changes</Button>
            <Button variant="primary" icon={Save} loading={saving} onClick={handleSave}>Save settings</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Report 58: Work schedule — the standard week, rest-day rules, extra time and the working calendar. */
function WorkScheduleTab() {
  return (
    <div className="space-y-4">
      <WorkStandardPanel />
      <Section title="Rest days" purpose="Every employee gets at least 24 hours of rest after six work days in a row, counted on their own schedule.">
        <ul className="max-w-[75ch] list-disc space-y-1 pl-5 text-body-sm text-secondary">
          <li>The business can open every day; each employee&apos;s schedule gives them their own rest day.</li>
          <li>A weekly schedule with no rest day, or a change that makes seven work days in a row, is refused.</li>
          <li>An existing schedule with no rest day holds that employee&apos;s payroll until it is fixed.</li>
          <li>Work on a rest day is recorded in Shifts as a one-day change marked &quot;rest-day work&quot;; payroll then pays the rest-day rate.</li>
        </ul>
        <SettingsRelatedLinks items={[{ to: "/business/hr/shifts", label: "Manage employee schedules" }]} />
      </Section>
      <Section title="Extra time after a shift" purpose="Every minute worked after a shift ends is kept, so HR can see it and decide on overtime.">
        <p className="max-w-[75ch] text-body-sm text-secondary">
          Only approved overtime is paid. Extra time that is not approved stays visible in attendance and in the payroll
          review; it is never dropped. Normal hours of work are at most eight a day; the overtime, rest-day, holiday and
          night rates are on Overtime, holidays &amp; night pay.
        </p>
        <SettingsRelatedLinks items={[{ to: "/business/hr/attendance", label: "View attendance records" }]} />
      </Section>
      <Section title="Working calendar" purpose="Official holidays and business closure days. Payroll, attendance and leave read them from the Calendar.">
        <SettingsRelatedLinks items={[{ to: "/business/administration/calendar", label: "Manage holidays and closures" }]} />
      </Section>
    </div>
  );
}

export default function HRGeneralSettings() {
  const { section } = useParams();
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await businessApi.getHRSettings();
      setSettings(response.settings);
      setError("");
    } catch (err) {
      setError(plainError(err, "The HR settings couldn't be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  if (searchParams.get("tab") === "holidays") return <Navigate to="/business/administration/calendar" replace />;
  const legacy = searchParams.get("section");
  if (!section) return <Navigate to={`${BASE}/${LEGACY_SECTIONS[legacy] || "general"}`} replace />;
  if (!SETTINGS_TABS.some((tab) => tab.key === section)) return <Navigate to={`${BASE}/general`} replace />;

  const handleSaveSettings = async (data) => {
    const response = await businessApi.updateHRSettings(data);
    setSettings(response.settings);
  };

  return (
    <HRPageShell title="HR Settings" subtitle="How work, pay rules, leave and government deductions are set up for this business.">
      <RouteTabs label="HR settings" className="mb-4" items={SETTINGS_TABS.map((tab) => ({ to: `${BASE}/${tab.key}`, label: tab.label }))} />
      {section === "work" ? <WorkScheduleTab /> : null}
      {section === "attendance-pay" ? <AttendancePayRulesPanel /> : null}
      {section === "pay-rules" ? <PayRulesPanel /> : null}
      {section === "government" ? <StatutoryRulesPanel /> : null}
      {["general", "leave"].includes(section) ? (
        loading && !settings ? <SkeletonRows rows={4} columns={2} label="Loading the HR settings" />
          : error && !settings ? <ErrorState title="HR settings couldn't be loaded" description={error} onRetry={loadSettings} />
            : <EditableSettings tab={section} settings={settings} onSave={handleSaveSettings} />
      ) : null}
    </HRPageShell>
  );
}
